import { lazy, Suspense, useState } from "react";
import { useIntl } from "react-intl";

import {
  DrawerContent,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { MruCountryProvider } from "@/components/Inputs/TelInput";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { TravellerCloseConfirmDrawer } from "@/features/travellers/components/TravellerCloseConfirmDrawer";
import {
  useNewTravellerMutation,
  useUpdateTravellerMutation,
} from "@/features/travellers/hooks/useTravellers";
import type { Traveller } from "@/features/travellers/types/types";
import type { TravellerAgeGroupConstraint } from "@/features/travellers/utils/validation";

import { useTravellerForm } from "./useTravellerForm";

const TravellerForm = lazy(
  () => import("@/features/travellers/components/TravellerForm"),
);

type TravellerFormDrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  travellerIdentifiableName?: string;
  traveller: Traveller | null;
  onSubmitSuccess: (traveller: Traveller, action: "create" | "update") => void;
  ageGroupConstraint?: TravellerAgeGroupConstraint;
};

export function TravellerFormDrawer({
  open,
  onClose,
  title,
  travellerIdentifiableName,
  traveller,
  onSubmitSuccess,
  ageGroupConstraint,
}: TravellerFormDrawerProps) {
  const intl = useIntl();
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);

  const handleMutationSuccess = (
    traveller: Traveller,
    action: "create" | "update",
  ) => {
    onSubmitSuccess(traveller, action);
  };
  const { mutateAsync: addTraveller, isPending: isAddPending } =
    useNewTravellerMutation({
      onSuccess: (traveller) => handleMutationSuccess(traveller, "create"),
    });
  const { mutateAsync: editTraveller, isPending: isEditPending } =
    useUpdateTravellerMutation({
      onSuccess: (traveller) => handleMutationSuccess(traveller, "update"),
    });

  const {
    control,
    isSubmitting,
    requestClose,
    scanned,
    scanningDrawerOpen,
    openScanningDrawer,
    closeScanningDrawer,
    onScanSuccess,
    onFormSubmit,
    resetForm,
    setupForm,
  } = useTravellerForm({
    traveller,
    ageGroupConstraint,
    onSubmit: (data) =>
      traveller
        ? editTraveller({ travellerId: traveller.id, data })
        : addTraveller(data),
    onClose: (reason) => {
      if (reason === "cancelWithPendingChanges") {
        setIsCloseConfirmOpen(true);
        return;
      }

      handleCloseDrawer();
    },
  });

  const handleCloseDrawer = () => {
    onClose();
    resetForm();
  };

  const travellerText = intl.formatMessage({
    defaultMessage: "traveller",
    description: "Noun to refer to a person who is traveling",
  });

  return (
    <>
      <SwipeableDrawer
        open={open}
        onOpened={setupForm}
        onClose={requestClose}
        disableRestoreFocus
        hasFullHeight
        backgroundColor="background"
      >
        <DrawerTopBar
          onCloseClick={requestClose}
          title={
            title ||
            (traveller
              ? intl.formatMessage(
                  {
                    defaultMessage: "Edit {name}",
                    description: "Label to edit a saved traveller's details",
                  },
                  {
                    name: travellerIdentifiableName ?? travellerText,
                  },
                )
              : intl.formatMessage(
                  {
                    defaultMessage: "Add {name}",
                    description: "Label to add a traveller to a booking slot",
                  },
                  {
                    name: travellerIdentifiableName ?? travellerText,
                  },
                ))
          }
        />
        <DrawerContent>
          <Suspense
            fallback={
              <PageLoader>
                <AnimatedLoader icon="users" />
              </PageLoader>
            }
          >
            <MruCountryProvider>
              <TravellerForm
                control={control}
                isSubmitting={isSubmitting || isAddPending || isEditPending}
                scanned={scanned}
                scanningDrawerOpen={scanningDrawerOpen}
                onScanClick={openScanningDrawer}
                onScanningDrawerClose={closeScanningDrawer}
                onScanSuccess={onScanSuccess}
                onFormSubmit={onFormSubmit}
              />
            </MruCountryProvider>
          </Suspense>
        </DrawerContent>
      </SwipeableDrawer>

      <TravellerCloseConfirmDrawer
        open={isCloseConfirmOpen}
        onClose={() => {
          setIsCloseConfirmOpen(false);
        }}
        onConfirm={() => {
          setIsCloseConfirmOpen(false);
          handleCloseDrawer();
        }}
      />
    </>
  );
}
